import { db } from "./firebase";
import { collection, doc, getDoc } from "firebase/firestore";
import { getAuth, fetchSignInMethodsForEmail } from "firebase/auth";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebase';
import {
    USER_ROLES,
    canInviteCarePartner,
    canInviteCaregiver,
    canInviteTherapist,
} from '../constants/roles';
import { getUserRoleForChild } from './rolePermissionService';
import { trackEvent, trackEventOnce } from './analytics';
import { buildInvitationLink } from './invitationLinks';

// Initialize Firebase Functions
const functions = getFunctions(app, 'us-central1');

// Get references to the callable Cloud Functions
const sendInvitationEmailCallable = httpsCallable(functions, 'sendInvitationEmail');
const resendInvitationEmailCallable = httpsCallable(functions, 'resendInvitationEmail');
const editInvitationEmailCallable = httpsCallable(functions, 'editInvitationEmail');
const cancelInvitationEmailCallable = httpsCallable(functions, 'cancelInvitationEmail');
const listChildInvitationsCallable = httpsCallable(functions, 'listChildInvitations');

export const getInvitationErrorMessage = (error = {}) => {
    const code = String(error?.code || '').replace(/^functions\//, '');
    const message = String(error?.message || '').trim();

    if (message && message.toUpperCase() !== 'INTERNAL') {
        return message;
    }
    if (code === 'failed-precondition') {
        return 'Invitation email is not configured correctly. Please contact support.';
    }
    if (code === 'resource-exhausted') {
        return 'Too many invitation emails were requested. Please try again shortly.';
    }
    if (code === 'unavailable') {
        return 'Invitation email is temporarily unavailable. Please try again.';
    }
    return 'We could not send the invitation email. Please try again.';
};

const canInviteRoleForChild = (requesterRole, targetRole) => {
    if (targetRole === USER_ROLES.CARE_PARTNER) {
        return canInviteCarePartner(requesterRole);
    }
    if (targetRole === USER_ROLES.CAREGIVER) {
        return canInviteCaregiver(requesterRole);
    }
    if (targetRole === USER_ROLES.THERAPIST) {
        return canInviteTherapist(requesterRole);
    }
    return false;
};

// Function to send an invitation to a team member
export const sendInvitation = async (childId, email, role, _specialization = null, personalMessage = null) => {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error("User not logged in.");
    }

    const requesterRole = await getUserRoleForChild(currentUser.uid, childId);
    const validRoles = [USER_ROLES.CARE_PARTNER, USER_ROLES.CAREGIVER, USER_ROLES.THERAPIST];
    if (!validRoles.includes(role)) {
        throw new Error(`Invalid role: ${role}. Can only invite Care Partners, Caregivers, or Therapists.`);
    }
    if (!canInviteRoleForChild(requesterRole, role)) {
        throw new Error("Access denied. You do not have permission to invite this role for this child.");
    }

    const senderName = currentUser.displayName || currentUser.email || 'A parent';
    const invitationId = doc(collection(db, "invitations")).id;

    // --- Fetch Child Name ---
    // This is crucial for the email content.
    let childName = 'A Child'; // Default placeholder
    try {
        const childDocRef = doc(db, "children", childId);
        const childDocSnap = await getDoc(childDocRef);
        if (childDocSnap.exists()) {
            childName = childDocSnap.data().name;
        } else {
            console.warn(`Child document with ID ${childId} not found.`);
        }
    } catch (error) {
        console.error("Error fetching child name:", error);
    }
    // --- End Fetch Child Name ---


    // Keep care details out of the visible URL. The backend resolves the
    // invitation id to the stored invitation record during acceptance.
    const invitationLink = buildInvitationLink(invitationId);

    try {
        const emailResult = await sendInvitationEmailCallable({
            recipientEmail: email,
            invitationId,
            childId,
            childName,
            role,
            senderName,
            senderUid: currentUser.uid,
            invitationLink,
            personalMessage
        });
        console.log('Email sent successfully:', emailResult.data);
        trackEvent("invite_sent", {
            role,
            status: "invited",
        });
        trackEventOnce("first_invite_sent", "first_invite_sent", {
            role,
            status: "invited",
        });
        return {
            status: "invited",
            invitationId: emailResult.data?.invitationId || invitationId,
            messageId: emailResult.data?.messageId || null,
            message: `Invitation email sent to ${email}.`
        };
    } catch (error) {
        console.error("Error sending invitation:", error);
        trackEvent("invite_send_failed", {
            role,
            error_code: error?.code || "unknown",
        });
        throw new Error(getInvitationErrorMessage(error), { cause: error });
    }
};

// Function to send a single invitation for multiple children
export const sendMultiChildInvitation = async (childIds, email, role, _specialization = null, personalMessage = null) => {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error("User not logged in.");
    }

    const validRoles = [USER_ROLES.CARE_PARTNER, USER_ROLES.CAREGIVER, USER_ROLES.THERAPIST];
    if (!validRoles.includes(role)) {
        throw new Error(`Invalid role: ${role}. Can only invite Care Partners, Caregivers, or Therapists.`);
    }

    for (const childId of childIds) {
        const requesterRole = await getUserRoleForChild(currentUser.uid, childId);
        if (!canInviteRoleForChild(requesterRole, role)) {
            throw new Error(`Access denied. You do not have permission to invite ${role} for all selected children.`);
        }
    }

    const senderName = currentUser.displayName || currentUser.email || 'A parent';
    const invitationId = doc(collection(db, "invitations")).id;

    // Fetch all child names
    const children = [];
    for (const childId of childIds) {
        try {
            const childDocRef = doc(db, "children", childId);
            const childDocSnap = await getDoc(childDocRef);
            if (childDocSnap.exists()) {
                children.push({
                    id: childId,
                    name: childDocSnap.data().name
                });
            }
        } catch (error) {
            console.error(`Error fetching child ${childId}:`, error);
        }
    }

    if (children.length === 0) {
        throw new Error("No valid children found for invitation");
    }

    const invitationLink = buildInvitationLink(invitationId);

    try {
        // Check if the user already exists
        const signInMethods = await fetchSignInMethodsForEmail(auth, email);
        const userExists = signInMethods && signInMethods.length > 0;

        if (userExists) {
            // User exists: Still send invitation email (they can just log in and get access)
            const emailResult = await sendInvitationEmailCallable({
                recipientEmail: email,
                invitationId,
                childIds: children.map(c => c.id),
                childNames: children.map(c => c.name), // Pass array of child names
                role,
                senderName,
                senderUid: currentUser.uid,
                invitationLink,
                personalMessage,
                multiChild: true // Flag for multi-child email template
            });
            console.log('Multi-child invitation email sent to existing user:', emailResult.data);
            return { 
                status: "invited_existing_user", 
                invitationId: emailResult.data?.invitationId || invitationId,
                messageId: emailResult.data?.messageId || null,
                message: `Invitation sent to existing user ${email} for ${children.length} children.`,
                children: children.map(c => c.name)
            };
        } else {
            // User doesn't exist: Send invitation to create account
            const emailResult = await sendInvitationEmailCallable({
                recipientEmail: email,
                invitationId,
                childIds: children.map(c => c.id),
                childNames: children.map(c => c.name),
                role,
                senderName,
                senderUid: currentUser.uid,
                invitationLink,
                personalMessage,
                multiChild: true
            });
            console.log('Multi-child invitation email sent to new user:', emailResult.data);
            return { 
                status: "invited_new_user", 
                invitationId: emailResult.data?.invitationId || invitationId,
                messageId: emailResult.data?.messageId || null,
                message: `Invitation sent to ${email} for ${children.length} children.`,
                children: children.map(c => c.name)
            };
        }
    } catch (error) {
        console.error("Error sending multi-child invitation:", error);
        throw new Error(getInvitationErrorMessage(error), { cause: error });
    }
};

export const listChildInvitations = async (childId) => {
    const result = await listChildInvitationsCallable({ childId });
    return result.data?.invitations || [];
};

export const resendInvitation = async (invitationId) => {
    try {
        const result = await resendInvitationEmailCallable({ invitationId });
        return result.data;
    } catch (error) {
        throw new Error(getInvitationErrorMessage(error), { cause: error });
    }
};

export const editInvitation = async (invitationId, recipientEmail, role, personalMessage = null) => {
    try {
        const result = await editInvitationEmailCallable({
            invitationId,
            recipientEmail,
            role,
            personalMessage,
        });
        return result.data;
    } catch (error) {
        throw new Error(getInvitationErrorMessage(error), { cause: error });
    }
};

export const cancelInvitation = async (invitationId, reason = 'canceled by owner') => {
    const result = await cancelInvitationEmailCallable({ invitationId, reason });
    return result.data;
};
